<script setup lang="ts">
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseDialog from '@/js/account/components/base/BaseDialog.vue';
import BaseTitle from '@/js/account/components/base/BaseTitle.vue';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    loading?: boolean
  }>(),
  {
    confirmLabel: 'Delete',
    cancelLabel: 'Cancel',
    loading: false,
  },
);

const emit = defineEmits<{
  confirm: []
  cancel: []
}>();

const open = defineModel<boolean>('open', { default: false });

const cn = classNames('confirm-dialog');

function onDismiss(): void {
  emit('cancel');
}

function onCancel(): void {
  if (props.loading) {
    return;
  }

  open.value = false;
  emit('cancel');
}

function onConfirm(): void {
  if (props.loading) {
    return;
  }

  emit('confirm');
}
</script>

<template>
  <BaseDialog
    v-model:open="open"
    :title="title"
    :close-locked="loading"
    :max-width="380"
    @close="onDismiss"
  >
    <div
      :class="cn()"
      role="alertdialog"
      :aria-describedby="`${cn()}-message`"
    >
      <p :id="`${cn()}-message`" :class="cn('message')">
        {{ message }}
      </p>

      <div :class="cn('actions')">
        <BaseButton
          theme="tertiary"
          size="md"
          :class="cn('action')"
          :disabled="loading"
          @click="onCancel"
        >
          {{ cancelLabel }}
        </BaseButton>
        <BaseButton
          theme="danger"
          size="md"
          :class="cn('action')"
          :loading="loading"
          :disabled="loading"
          @click="onConfirm"
        >
          {{ confirmLabel }}
        </BaseButton>
      </div>
    </div>
  </BaseDialog>
</template>

<style lang="scss" scoped>
.confirm-dialog {
  &__message {
    margin: 0 0 24px;
    color: var(--color-text-secondary);
    font-family: var(--font-family-sans);
    line-height: 1.3;
    font-weight: 300;
    font-size: 16px;
    text-align: left;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  &__action {
    max-width: none;
    width: 100%;
  }
}
</style>
